/**
 * UI review Medium (Engagement, Fleet, AI & translation): workspace-wide settings kept in collab.workspaces.settings,
 * one object per section. The API enforces each value; the web app only reads them to explain what is switched off.
 */
import { EMAIL_DELAY_STANDARD, isEmailDelayChoice } from './emailDelay';

export interface WorkspacePrefs {
  engagement: { birthday_posts: boolean; birthday_channel_slug: string; sales_target_posts: boolean };
  ai: { translation: boolean; assistant: boolean; digests: boolean; meeting_notes: boolean; catch_up: boolean };
  fleet: { reminder_days: number[]; magic_link_hours: number; tracking_consent_text: string; /** t494u: where journey updates go when a building has no channel of its own. */ delivery_channel_id: string | null;
    /** t518u: a linked thread gets a new ETA reply when the ETA moves by at least this many minutes. */ eta_update_threshold_min: number;
    /** t541u: "Stopped for a while" after this many minutes within ~100 m away from planned stops and the depot. */
    stopped_alert_minutes: number;
    /** t541u: also tell the journey's planner (off by default). */
    stopped_notify_manager: boolean;
    /** t541u: journey location history is deleted after this many days (nightly). */
    position_retention_days: number };
  /** Size limits: people in a channel, in a group DM (incl. you) and in one call (also capped by the LiveKit ceiling). */
  chat: { channel_member_limit: number; room_member_limit: number; group_dm_limit: number; call_participant_limit: number };
  /** t566u: minutes before an unseen notification is emailed, for people who haven't chosen their own (0 = never). */
  notifications: { email_delay_minutes: number };
}
export type WorkspacePrefSection = keyof WorkspacePrefs;
export const WORKSPACE_PREF_SECTIONS: WorkspacePrefSection[] = ['engagement', 'ai', 'fleet', 'chat', 'notifications'];

/** Hard bounds for the chat limits. Calls are also capped by the server's LiveKit ceiling (LIVEKIT_MAX_PARTICIPANTS). */
export const CHAT_LIMIT_BOUNDS = {
  channel_member_limit: { min: 2, max: 5000, label: 'People in a channel' },
  room_member_limit: { min: 2, max: 1000, label: 'People in an ad-hoc meeting room' },
  group_dm_limit: { min: 3, max: 50, label: 'People in a group message' },
  call_participant_limit: { min: 2, max: 100, label: 'People in a call' },
} as const;
export const DEFAULT_CHAT_LIMITS: WorkspacePrefs['chat'] = { channel_member_limit: 100, room_member_limit: 100, group_dm_limit: 50, call_participant_limit: 50 };

export const DEFAULT_TRACKING_CONSENT = 'Share my location during this run (for store ETAs)';
export const DEFAULT_WORKSPACE_PREFS: WorkspacePrefs = {
  engagement: { birthday_posts: true, birthday_channel_slug: 'general', sales_target_posts: true },
  ai: { translation: true, assistant: true, digests: true, meeting_notes: true, catch_up: true },
  fleet: { reminder_days: [30, 7], magic_link_hours: 24, tracking_consent_text: DEFAULT_TRACKING_CONSENT, delivery_channel_id: null, eta_update_threshold_min: 15, stopped_alert_minutes: 15, stopped_notify_manager: false, position_retention_days: 90 },
  chat: DEFAULT_CHAT_LIMITS,
  notifications: { email_delay_minutes: EMAIL_DELAY_STANDARD },
};

/** Who may change each section (read is open to every member: nothing here is secret). */
export const WORKSPACE_PREF_PERMS: Record<WorkspacePrefSection, string> = {
  engagement: 'kudos.badges.manage',
  ai: 'admin.roles.manage',
  fleet: 'fleet.vehicles.manage',
  chat: 'admin.roles.manage',
  notifications: 'admin.roles.manage',
};

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Settings jsonb → prefs with defaults. The birthday channel keeps its original top-level key. */
export function readWorkspacePrefs(settings: unknown): WorkspacePrefs {
  const s = isObj(settings) ? settings : {};
  const sec = <K extends WorkspacePrefSection>(k: K): WorkspacePrefs[K] => ({ ...DEFAULT_WORKSPACE_PREFS[k], ...(isObj(s[k]) ? (s[k] as Partial<WorkspacePrefs[K]>) : {}) });
  const engagement = sec('engagement');
  if (typeof s.birthday_channel_slug === 'string' && s.birthday_channel_slug) engagement.birthday_channel_slug = s.birthday_channel_slug;
  const notifications = sec('notifications');
  if (!isEmailDelayChoice(notifications.email_delay_minutes)) notifications.email_delay_minutes = EMAIL_DELAY_STANDARD;
  return { engagement, ai: sec('ai'), fleet: sec('fleet'), chat: sec('chat'), notifications };
}

/** Validate a partial update for one section. Unknown keys are refused so typos don't silently do nothing. */
export function validatePrefPatch(section: WorkspacePrefSection, patch: unknown): { value: Record<string, unknown>; errors: string[] } {
  const errors: string[] = [];
  const value: Record<string, unknown> = {};
  if (!isObj(patch)) return { value, errors: ['Send an object of settings'] };
  const defaults = DEFAULT_WORKSPACE_PREFS[section] as Record<string, unknown>;
  for (const [k, v] of Object.entries(patch)) {
    if (!(k in defaults)) { errors.push(`Unknown setting "${k}"`); continue; }
    const d = defaults[k];
    if (typeof d === 'boolean') { if (typeof v !== 'boolean') errors.push(`${k} must be true or false`); else value[k] = v; continue; }
    if (k === 'birthday_channel_slug') { if (typeof v !== 'string' || !/^[a-z0-9][a-z0-9-_]{0,79}$/.test(v)) errors.push('Pick a channel'); else value[k] = v; continue; }
    if (k === 'reminder_days') {
      if (!Array.isArray(v) || !v.length || v.length > 4 || !v.every(n => Number.isInteger(n) && n >= 1 && n <= 180)) errors.push('Reminder days: 1 to 4 whole numbers between 1 and 180');
      else value[k] = [...new Set(v as number[])].sort((a, b) => b - a);
      continue;
    }
    if (k === 'eta_update_threshold_min') { if (!Number.isInteger(v) || (v as number) < 5 || (v as number) > 240) errors.push('ETA update threshold: a whole number of minutes from 5 to 240'); else value[k] = v; continue; }
    if (k === 'magic_link_hours') { if (!Number.isInteger(v) || (v as number) < 1 || (v as number) > 168) errors.push('Run links last 1 to 168 hours'); else value[k] = v; continue; }
    if (k === 'stopped_alert_minutes') { if (!Number.isInteger(v) || (v as number) < 5 || (v as number) > 240) errors.push('Stopped for a while: 5 to 240 minutes'); else value[k] = v; continue; }
    if (k === 'position_retention_days') { if (!Number.isInteger(v) || (v as number) < 1 || (v as number) > 730) errors.push('Keep location history: 1 to 730 days'); else value[k] = v; continue; }
    if (k in CHAT_LIMIT_BOUNDS) {
      const b = CHAT_LIMIT_BOUNDS[k as keyof typeof CHAT_LIMIT_BOUNDS];
      if (!Number.isInteger(v) || (v as number) < b.min || (v as number) > b.max) errors.push(`${b.label}: a whole number from ${b.min} to ${b.max}`); else value[k] = v;
      continue;
    }
    if (k === 'email_delay_minutes') { if (!isEmailDelayChoice(v)) errors.push('Email after: pick one of the listed times, or Never'); else value[k] = v; continue; }
    if (k === 'delivery_channel_id') { if (v !== null && (typeof v !== 'string' || !/^[0-9a-f-]{36}$/i.test(v))) errors.push('Pick a channel, or none'); else value[k] = v; continue; }
    if (k === 'tracking_consent_text') { if (typeof v !== 'string' || !v.trim() || v.length > 300) errors.push('Consent text: 1 to 300 characters'); else value[k] = v.trim(); continue; }
  }
  return { value, errors };
}
